import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

export type ContentAccessStatus = Readonly<{
  contentHidden: boolean
  contentBlocked: boolean
}>

const CONTENT_ACCESS_STATUS_KEY = 'content-access-status'

export type GetCurrentRestrictedState = (
  queryClient: QueryClient
) => ContentAccessStatus

export const OPEN_CONTENT_ACCESS_STATUS: ContentAccessStatus = {
  contentHidden: false,
  contentBlocked: false,
}

const getCurrentContentAccessStatus = (queryClient: QueryClient) =>
  queryClient.getQueryData<ContentAccessStatus>([CONTENT_ACCESS_STATUS_KEY]) ??
  OPEN_CONTENT_ACCESS_STATUS

const useGetContentAccessStatus = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const unsubscribeFnRef = useRef<() => void>(undefined)

  const [contentAccessStatus, setContentAccessStatus] =
    useState<ContentAccessStatus>(() =>
      getCurrentContentAccessStatus(queryClient)
    )

  const updateGetCurrentContentAccessStatus = useCallback(
    (args: any) => {
      if (
        ['invalidate'].includes(args?.action?.type) &&
        args.query.queryKey[0] === CONTENT_ACCESS_STATUS_KEY
      ) {
        setContentAccessStatus(getCurrentContentAccessStatus(queryClient))
      }
    },
    [queryClient]
  )

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(
      updateGetCurrentContentAccessStatus
    )

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [updateGetCurrentContentAccessStatus, queryCache])

  return contentAccessStatus
}

export default useGetContentAccessStatus
